import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Vallejo", cities: "Vallejo, Benicia, American Canyon, Mare Island" },
    { zone: "Fairfield", cities: "Fairfield, Suisun City, Cordelia, Green Valley" },
    { zone: "Vacaville", cities: "Vacaville, Dixon, Elmira, Browns Valley" },
    { zone: "Benicia", cities: "Benicia, Rio Vista, Port Costa, Crockett" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Solano</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en Vallejo, Fairfield, Vacaville y todo el Condado de Solano.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Solano</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

const SolanoSpanishPage = () => {
  return (
    <LocationContext.Provider value={{ slug: "solano", name: "Solano", type: "region", regionSlug: "solano" }}>
      <SEO
        title="Limpieza del Hogar en Condado de Solano, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Vallejo, Fairfield, Vacaville, Benicia y todo el Condado de Solano. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar solano, servicio de limpieza vallejo, limpieza profunda fairfield ca, empleada doméstica vacaville, maid service solano county español, limpieza benicia suisun city dixon, servicio de limpieza condado solano"
        url="https://uplevelcleaning.com/es/solano"
        locationName="Solano"
        regionSlug="solano"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Condado de Solano", url: "/es/solano" }
        ]}
        reviews={[
          { author: "Leticia G.", rating: 5, reviewBody: "Vivo en Vallejo y encontrar un servicio de limpieza de confianza era difícil. ¡Uplevel Cleaning cambió todo eso! Puntuales, amables y con resultados perfectos.", datePublished: "2026-03-22" },
          { author: "Ernesto C.", rating: 5, reviewBody: "Contraté el servicio mensual para nuestra casa en Fairfield. El equipo siempre llega a tiempo y deja todo impecable. Precio exacto sin sorpresas.", datePublished: "2026-02-10" },
          { author: "Blanca H.", rating: 5, reviewBody: "La limpieza de mudanza en Vacaville fue perfecta. El arrendador quedó muy impresionado y recuperamos el depósito completo. ¡Gracias, Uplevel!", datePublished: "2026-01-15" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Solano" regionSlug="solano" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Condado de Solano",
                "Vallejo, Fairfield, Vacaville y Más",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Condado de Solano" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="solano" />
          <Guarantee />
          <SpanishFAQ cityName="Condado de Solano" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Condado de Solano" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SolanoSpanishPage;
